<script>
import { mapGetters } from "vuex"

export default {
  name: "Series",

  beforeRouteUpdate(to, from, next) {
    // Call the API query method when the URL changes
    this.getSeries()
    next()
  },

  data() {
    return {
      series: {
        installationSteps: {
          title: "Comment faire ?",
          description: "Les étapes de l'installation photovoltaïque",
          to: { query: { series: "installationSteps" } },
          tags: ["installationSteps"],
        },
        introductions: {
          title: "Guide solaire",
          description: "Discover more about why you should go solar",
          to: { query: { series: "introductions" } },
          tags: ["introductions"],
        },
        stories: {
          title: "Témoignages",
          description: "Explore others solar journey",
          to: { query: { series: "stories" } },
          tags: ["stories"],
        },
        all: {
          title: "Tous",
          description:
            "Retrouvez tous les articles et astuces de la rédaction chez projetsolaire.",
          to: { name: "BlogSeries" },
          tags: ["blogArticle"],
        },
      },
    }
  },

  computed: {
    ...mapGetters({
      getPreviewsByTagInclusion: "content/getPreviewsByTagInclusion",
    }),

    entries() {
      const filteredSeries = Object.keys(this.series).filter(
        (tab) => tab === "installationSteps"
      )
      const filteredTags = filteredSeries
        .map((series) => this.series[series].tags)
        .flat()
      return this.getPreviewsByTagInclusion(filteredTags)
    },

    tags() {
      return this.currentSeries.tags
    },

    currentSeries() {
      return this.$route.query.series
        ? this.series[this.$route.query.series]
        : this.series["all"]
    },

    breadcrumbs() {
      return [
        {
          name: this.currentSeries.title,
          to: this.currentSeries.to,
          current: true,
        },
      ]
    },
    filteredSeries() {
      return Object.keys(this.series).filter(
        (tab) => tab === "installationSteps"
      )
    },
  },

  created() {
    // Call the API query method on page load
    this.getSeries()
  },

  methods: {
    async getSeries() {
      await this.$store.dispatch("content/fetchAllPreviewsByTagInclusion", {
        type: "article",
        tags: this.tags,
      })
    },
  },
}
</script>

<template>
  <section class="relative">
    <!-- Section background (needs .relative class on parent and next sibling elements) -->
    <div
      class="absolute inset-0 bg-transparent pointer-events-none mb-16"
      aria-hidden="true"
    ></div>
    <div
      class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-40"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#FDB777] to-[#FF6200] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        style="
          clip-path: polygon(
            74.1% 44.1%,
            100% 61.6%,
            97.5% 26.9%,
            85.5% 0.1%,
            80.7% 2%,
            72.5% 32.5%,
            60.2% 62.4%,
            52.4% 68.1%,
            47.5% 58.3%,
            45.2% 34.5%,
            27.5% 76.7%,
            0.1% 64.9%,
            17.9% 100%,
            27.6% 76.8%,
            76.1% 97.7%,
            74.1% 44.1%
          );
        "
      />
    </div>
    <div
      class="absolute left-0 right-0 m-auto w-px p-px h-20 bg-gray-200 transform -translate-y-1/2"
    ></div>

    <div class="relative max-w-7xl mx-auto py-12 px-4 sm:px-6">
      <div class="pt-5 md:pt-5">
        <div class="max-w-4xl mx-auto text-center pb-8 md:pb-12">
          <h1 class="h2">{{ $t("home.how-to.title") }}</h1>
          <p class="text-xl text-gray-600 mt-4">
            {{ $t("home.how-to.description-1") }}
          </p>
          <p class="text-xl text-gray-600 mt-2">
            {{ $t("home.how-to.description-2") }}
          </p>
        </div>
        <div class="mx-auto grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
          <div
            v-for="post in entries"
            :key="post.title"
            class="flex flex-col overflow-hidden rounded-lg shadow-lg"
          >
            <router-link
              class="mt-2 block"
              :to="{
                name: 'BlogArticle',
                params: { slug: post.fields.slug },
              }"
            >
              <div class="flex-shrink-0">
                <img
                  class="h-48 w-full object-cover"
                  :src="
                    post.fields.thumbnailImage
                      ? post.fields.thumbnailImage.fields.file.url
                      : 'https://www.sat-electric.fi/wp-content/uploads/2019/11/placeholder.png'
                  "
                  alt=""
                />
              </div>
              <div class="flex flex-1 flex-col justify-between bg-white p-6">
                <div class="flex-1">
                  <p class="text-sm font-medium text-indigo-600">
                    <a
                      v-if="post.category"
                      :href="post.category.href"
                      class="hover:underline"
                      >{{ post.category.name }}</a
                    >
                  </p>

                  <p class="text-xl font-semibold text-gray-900">
                    {{ post.fields.title }}
                  </p>
                  <p class="mt-3 text-base text-gray-500">
                    {{ post.fields.description }}
                  </p>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <div
      class="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#FDB777] to-[#FF6200] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
        style="
          clip-path: polygon(
            74.1% 44.1%,
            100% 61.6%,
            97.5% 26.9%,
            85.5% 0.1%,
            80.7% 2%,
            72.5% 32.5%,
            60.2% 62.4%,
            52.4% 68.1%,
            47.5% 58.3%,
            45.2% 34.5%,
            27.5% 76.7%,
            0.1% 64.9%,
            17.9% 100%,
            27.6% 76.8%,
            76.1% 97.7%,
            74.1% 44.1%
          );
        "
      />
    </div>
  </section>
</template>

<style>
.blob {
  width: 1000px;
  height: 1000px;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url("data:image/svg+xml;utf8, %3Csvg width=%22100%25%22 height=%22100%25%22 viewBox=%220 0 1000 1000%22 xmlns=%22http:%2F%2Fwww.w3.org%2F2000%2Fsvg%22 %3E %3Cdefs%3E %3ClinearGradient id=%22linearGradientId%22 gradientTransform=%22rotate(-45 0.5 0.5)%22%3E %3Cstop offset=%220%25%22 stop-color=%22%23FAD961%22 %2F%3E %3Cstop offset=%22100%25%22 stop-color=%22%23F76B1C%22 %2F%3E %3C%2FlinearGradient%3E %3CclipPath id=%22shape%22%3E %3Cpath fill=%22currentColor%22 d=%22M778.5%2C595Q690%2C690%2C595%2C772Q500%2C854%2C327%2C850Q154%2C846%2C114%2C673Q74%2C500%2C136%2C349Q198%2C198%2C349%2C211.5Q500%2C225%2C667%2C195.5Q834%2C166%2C850.5%2C333Q867%2C500%2C778.5%2C595Z%22%3E%3C%2Fpath%3E %3C%2FclipPath%3E %3C%2Fdefs%3E %3Cg clip-path=%22url(%23shape)%22%3E %3Cpath fill=%22url(%23linearGradientId)%22 d=%22M778.5%2C595Q690%2C690%2C595%2C772Q500%2C854%2C327%2C850Q154%2C846%2C114%2C673Q74%2C500%2C136%2C349Q198%2C198%2C349%2C211.5Q500%2C225%2C667%2C195.5Q834%2C166%2C850.5%2C333Q867%2C500%2C778.5%2C595Z%22 %2F%3E %3C%2Fg%3E %3C%2Fsvg%3E");
}
</style>
